import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { Controller } from "react-hook-form";

import LoginFullLogoMark from "@/assets/login/full-logo-mark.svg?react";
import LoginLogoMark from "@/assets/login/logo-mark.svg?react";
import { Button } from "@/components/button";
import { Checkbox, CheckboxField } from "@/components/checkbox";
import { ErrorBoundary } from "@/components/error-boundary";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/field";
import { useTheme } from "@/hooks/useTheme";
import { useWhitelabel } from "@/hooks/useWhitelabel";
import { initIntercom } from "@/lib/intercom";

import type { LoginFormData } from "./useLoginForm";

import { AuthContainer } from "../components/AuthContainer";
import { LoginPromoCarousel } from "./components/LoginPromoCarousel";
import { PRIVACY_POLICY_LINK, SIGNUP_LINK, TOC_LINK } from "./consts";
import { TermsPrivacyAgreementModal } from "./TermsPrivacyAgreementModal";
import { useLoginForm } from "./useLoginForm";
import { useLoginMutation } from "./useLoginMutation";
import { usePostLoginRedirect } from "./usePostLoginRedirect";

export function LoginForm() {
  useTheme();

  const [isOpen, setIsOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { control, handleSubmit, setError } = useLoginForm();
  const { isPending, mutate } = useLoginMutation();
  const navigatePostLogin = usePostLoginRedirect();
  const queryClient = useQueryClient();

  // Whitelabel-aware links
  const { config, isWhitelabel, loading } = useWhitelabel();
  const tocLink = isWhitelabel ? config?.termsUrl : TOC_LINK;
  const privacyLink = isWhitelabel ? config?.privacyUrl : PRIVACY_POLICY_LINK;
  const signupLink = isWhitelabel ? undefined : SIGNUP_LINK;

  function onSubmit(data: LoginFormData) {
    mutate(data, {
      onError: (error) => {
        const isCredentialError =
          (error as { statusCode?: number }).statusCode === 401;
        const message = isCredentialError
          ? "Incorrect email or password. Please try again."
          : "Something went wrong on our end. Please try again later.";

        setError("email", { message: " ", type: "manual" });
        setError("password", { message, type: "manual" });
      },
      onSuccess: async (data) => {
        if (data.agreement) {
          await queryClient.invalidateQueries({ queryKey: ["user"] });
          initIntercom();
          navigatePostLogin();
        } else {
          setIsOpen(true);
        }
      },
    });
  }

  const emailField = (
    <Controller
      control={control}
      name="email"
      render={({
        field: { name, onBlur, onChange, ref, value },
        fieldState: { error, invalid },
      }) => (
        <TextField
          isInvalid={invalid}
          isRequired
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          validationBehavior="aria"
          value={value}
        >
          <Label>Email</Label>
          <Input
            className="border-login-border"
            placeholder="Enter your email"
            ref={ref}
          />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
      rules={{ required: "Email is required." }}
    />
  );

  const whitelabelEmailField = (
    <Controller
      control={control}
      name="email"
      render={({
        field: { name, onBlur, onChange, ref, value },
        fieldState: { error, invalid },
      }) => (
        <TextField
          isInvalid={invalid}
          isRequired
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          validationBehavior="aria"
          value={value}
        >
          <Label>Email</Label>
          <Input placeholder="Enter your email" ref={ref} />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
      rules={{ required: "Email is required." }}
    />
  );

  const passwordField = (
    <Controller
      control={control}
      name="password"
      render={({
        field: { name, onBlur, onChange, ref, value },
        fieldState: { error, invalid },
      }) => (
        <TextField
          isInvalid={invalid}
          isRequired
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          validationBehavior="aria"
          value={value}
        >
          <Label>Password</Label>
          <div className="relative">
            <Input
              className="border-login-border pr-10"
              placeholder="Enter your password"
              ref={ref}
              type={showPassword ? "text" : "password"}
            />
            <button
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-3 flex items-center text-text-tertiary hover:text-text-secondary"
              onClick={() => setShowPassword((prev) => !prev)}
              tabIndex={-1}
              type="button"
            >
              {showPassword ? (
                <EyeOff className="size-5" />
              ) : (
                <Eye className="size-5" />
              )}
            </button>
          </div>
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
      rules={{ required: "Password is required." }}
    />
  );

  const whitelabelPasswordField = (
    <Controller
      control={control}
      name="password"
      render={({
        field: { name, onBlur, onChange, ref, value },
        fieldState: { error, invalid },
      }) => (
        <TextField
          isInvalid={invalid}
          isRequired
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          validationBehavior="aria"
          value={value}
        >
          <Label>Password</Label>
          <Input placeholder="Enter your password" ref={ref} type="password" />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
      rules={{ required: "Password is required." }}
    />
  );

  const whitelabelRememberMeAndForgotPasswordRow = (
    <div className="flex justify-between">
      <Controller
        control={control}
        name="rememberMe"
        render={({ field }) => (
          <CheckboxField>
            <Checkbox onChange={field.onChange}>Remember me</Checkbox>
            <Description>Save login for 30 days</Description>
          </CheckboxField>
        )}
      />

      <Link
        className="h-min text-sm font-semibold text-utility-blue-500"
        to="/forgot-password"
      >
        Forgot password?
      </Link>
    </div>
  );

  const rememberMeAndForgotPasswordRow = (
    <div className="flex justify-between">
      <Controller
        control={control}
        name="rememberMe"
        render={({ field }) => (
          <CheckboxField>
            <Checkbox
              indicatorClassName="border-login-button-bg bg-login-button-bg"
              onChange={field.onChange}
            >
              Remember me
            </Checkbox>
            <Description>Save login for 30 days</Description>
          </CheckboxField>
        )}
      />

      <Link
        className="h-min text-sm font-semibold text-login-heading"
        to="/forgot-password"
      >
        Forgot password?
      </Link>
    </div>
  );

  const whitelabelSignUpRow = (
    <span className="mx-auto inline-block text-sm font-medium text-text-secondary">
      {signupLink && (
        <>
          Don&apos;t have an account?{" "}
          <a
            className="h-min text-sm font-semibold text-utility-blue-500"
            href={signupLink}
          >
            Sign up
          </a>
        </>
      )}
    </span>
  );

  const signUpRow = (
    <span className="mx-auto inline-block text-sm font-medium text-login-body">
      {signupLink && (
        <>
          Don&apos;t have an account?{" "}
          <a
            className="h-min text-sm font-semibold text-login-heading"
            href={signupLink}
            rel="noopener noreferrer"
            target="_blank"
          >
            Sign up!
          </a>
        </>
      )}
    </span>
  );

  if (loading) {
    return null;
  }

  if (isWhitelabel) {
    return (
      <AuthContainer
        description="Please enter your login details below"
        footer={
          <>
            {tocLink && (
              <a
                className="text-text-secondary underline underline-offset-2"
                href={tocLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms of use
              </a>
            )}
            {privacyLink && (
              <a
                className="text-text-secondary underline underline-offset-2"
                href={privacyLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy policy
              </a>
            )}
          </>
        }
        title="Platform login"
      >
        <TermsPrivacyAgreementModal isOpen={isOpen} setIsOpen={setIsOpen} />

        <form
          className="flex flex-col gap-3xl"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-xl">
            {whitelabelEmailField}
            {whitelabelPasswordField}
          </div>
          {whitelabelRememberMeAndForgotPasswordRow}
          <Button isDisabled={isPending} type="submit">
            {isPending ? "Signing in..." : "Sign in"}
          </Button>
          {whitelabelSignUpRow}
        </form>
      </AuthContainer>
    );
  }

  return (
    <div className="relative flex min-h-screen w-full justify-center bg-white lg:items-center">
      <LoginFullLogoMark className="absolute left-[39px] top-[34px] hidden h-auto w-[123px] lg:block" />
      <div className="flex w-full max-w-[1400px] flex-col items-center justify-center gap-4xl px-2xl py-4xl lg:flex-row lg:h-[clamp(750px,92vh,960px)] lg:items-stretch lg:gap-[48px] lg:px-[100px] lg:py-xl">
        <div className="flex w-full flex-col items-center justify-between lg:flex-1 xl:min-w-[480px]">
          <div className="flex w-full flex-1 flex-col items-center justify-center">
            <div className="flex w-full max-w-[440px] flex-col gap-4xl">
              <div className="flex flex-col items-center gap-2xl">
                <LoginLogoMark className="size-[57px]" />
                <h1 className="text-center text-display-xs font-bold text-login-heading lg:text-display-sm">
                  Login to your account
                </h1>
                <p className="text-center text-md text-login-body">
                  Please enter your login details below
                </p>
              </div>

              <TermsPrivacyAgreementModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
              />

              <form
                className="flex flex-col gap-3xl"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="flex flex-col gap-xl">
                  {emailField}
                  {passwordField}
                </div>
                {rememberMeAndForgotPasswordRow}
                <Button
                  className="bg-login-button-bg hover:bg-login-button-bg/90"
                  isDisabled={isPending}
                  type="submit"
                >
                  {isPending ? "Signing in..." : "Sign in"}
                </Button>
                {signUpRow}
              </form>
            </div>
          </div>

          <div className="flex justify-center gap-2xl pt-4xl">
            {tocLink && (
              <a
                className="text-xs text-login-body underline underline-offset-2"
                href={tocLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms of use
              </a>
            )}
            {privacyLink && (
              <a
                className="text-xs text-login-body underline underline-offset-2"
                href={privacyLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy policy
              </a>
            )}
          </div>
        </div>

        <div className="hidden lg:flex lg:flex-1 lg:items-stretch lg:justify-center xl:basis-[544px] xl:grow-0">
          <ErrorBoundary fallback={null}>
            <LoginPromoCarousel />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}
